/**
 * HFP v4 form logic in the browser (TDI-525)
 * ------------------------------------------
 * Which screens a parent or teacher sees, in what order, and whether each is
 * answered. Show-and-hide only (ruling D86): the browser evaluates the
 * compiled predicates the server serves in the form bundle and never scores,
 * flags or suggests anything. The server re-runs the same rules at
 * submission and refuses an unanswered screen (D83).
 *
 * The answers are held in the draft-save shape, keyed as rmsSaveHfpV4Draft
 * takes them (form specification section 11):
 *   "b1400": "b1400.m2"            a core, sensory or context question
 *   "F1.1": "F1.1.2"               a single-choice follow-on step
 *   "F1.2": ["F1.2.1", "F1.2.3"]   a tick-all follow-on step
 *   "F5:F5.1.2": "F5.2.1"          a support grid row: a rating answer ID,
 *                                  the row's would-help twin ID, "have",
 *                                  "none" or "not_sure"
 *
 * The order (specification section 4 and 5.3): the core questions in the
 * bundle's order with each sensory follow-up straight after its base when the
 * base answer opens it; then the "about them" questions; then each follow-on
 * module that opened, in sheet order except F10 and F11, which move to
 * straight after F4 when F4 opened them (C3); each module's single and tick
 * steps that show, then its one support grid.
 */

export const GRID_HAVE = 'have';
export const GRID_NONE = 'none';
export const GRID_NOT_SURE = 'not_sure';

/** The reference evaluator (model scripts/display.py run_compiled), ported. */
export function evaluatePredicate(predicate, answers, ticks, display) {
  if (!predicate || typeof predicate !== 'object') return false;
  const [op] = Object.keys(predicate);
  const v = predicate[op];
  switch (op) {
    case 'const':
      return Boolean(v);
    case 'all':
      return v.every((p) => evaluatePredicate(p, answers, ticks, display));
    case 'any':
      return v.some((p) => evaluatePredicate(p, answers, ticks, display));
    case 'not':
      return !evaluatePredicate(v, answers, ticks, display);
    case 'at_least':
      return v.of.filter((p) => evaluatePredicate(p, answers, ticks, display)).length >= v.n;
    case 'in':
      return v.ids.includes(answers[v.q]);
    case 'has':
      return v.ids.some((id) => ticks.has(id));
    case 'open':
      return evaluatePredicate(display[`trigger:${v}`], answers, ticks, display);
    default:
      throw new Error(`Unknown predicate operator: ${op}`);
  }
}

/** Single answers by question or step ID, as the predicates read them. */
export function singleAnswers(saved) {
  const out = {};
  Object.entries(saved || {}).forEach(([key, value]) => {
    if (typeof value === 'string' && !key.includes(':')) out[key] = value;
  });
  return out;
}

function gridsOf(bundle) {
  return ((bundle && bundle.followons) || []).map((m) => m.grid).filter(Boolean);
}

export function gridRowKey(gridId, rowId) {
  return `${gridId}:${rowId}`;
}

/**
 * Every ticked option ID: the tick-all steps' ticks, and what the support
 * grids stand for in the old steps (C5): the in-place option for a row
 * answered "We have this" or rated, the would-help twin for "would help".
 */
export function tickedOptions(saved, bundle) {
  const ticks = new Set();
  Object.values(saved || {}).forEach((value) => {
    if (Array.isArray(value)) value.forEach((id) => ticks.add(id));
  });
  gridsOf(bundle).forEach((grid) => {
    const ratingIds = new Set((grid.ratingAnswers || []).map((a) => a.id));
    (grid.rows || []).forEach((row) => {
      const value = (saved || {})[gridRowKey(grid.id, row.id)];
      if (value === GRID_HAVE || ratingIds.has(value)) ticks.add(row.id);
      const twin = (grid.wouldHelpTwins || {})[row.id];
      if (twin && value === twin) ticks.add(twin);
    });
  });
  return ticks;
}

export const F4_LIGHT_OPTION = 'F4.1.6';
export const F4_NOISE_OPTION = 'F4.1.7';

/** Display order of the opened modules (C3, ruled by Tom 28 Oct). */
export function followonOrder(sheetOrder, opened, ticks) {
  const shown = sheetOrder.filter((id) => opened.includes(id));
  if (!shown.includes('F4')) return shown;
  const moved = [['F10', F4_LIGHT_OPTION], ['F11', F4_NOISE_OPTION]]
    .filter(([id, option]) => shown.includes(id) && ticks.has(option))
    .map(([id]) => id);
  if (moved.length === 0) return shown;
  const rest = shown.filter((id) => !moved.includes(id));
  const at = rest.indexOf('F4') + 1;
  return [...rest.slice(0, at), ...moved, ...rest.slice(at)];
}

/**
 * The screens this form shows for the answers so far, in order. Each entry:
 *   { type: 'question', screen }         core, sensory or context question
 *   { type: 'step', step, module }        single or tick follow-on step
 *   { type: 'grid', grid, module }        a module's support grid
 * Every entry is one counted, timed screen (specification section 7).
 */
export function buildSequence(bundle, saved) {
  if (!bundle) return [];
  const display = bundle.display || {};
  const answers = singleAnswers(saved);
  const ticks = tickedOptions(saved, bundle);
  const holds = (key) => evaluatePredicate(display[key], answers, ticks, display);
  const sequence = [];

  (bundle.screens || []).forEach((screen) => {
    if (screen.shownWhen && !evaluatePredicate(screen.shownWhen, answers, ticks, display)) return;
    sequence.push({ type: 'question', id: screen.id, screen });
  });
  (bundle.context || []).forEach((screen) => {
    sequence.push({ type: 'question', id: screen.id, screen });
  });

  const modules = bundle.followons || [];
  const byId = Object.fromEntries(modules.map((m) => [m.id, m]));
  const opened = modules.filter((m) => holds(m.predicate)).map((m) => m.id);
  followonOrder(modules.map((m) => m.id), opened, ticks).forEach((moduleId) => {
    const module = byId[moduleId];
    (module.steps || []).forEach((step) => {
      if (step.predicate in display && !holds(step.predicate)) return;
      sequence.push({ type: 'step', id: step.id, step, module });
    });
    if (module.grid && holds(module.grid.predicate)) {
      sequence.push({ type: 'grid', id: `${module.id}:grid`, grid: module.grid, module });
    }
  });
  return sequence;
}

/** Whether one screen holds an answer or Not sure (D83: no blank screens). */
export function isScreenAnswered(entry, saved) {
  const values = saved || {};
  if (!entry) return false;
  if (entry.type === 'grid') {
    const rows = entry.grid.rows || [];
    return rows.length > 0 && rows.every((row) => Boolean(values[gridRowKey(entry.grid.id, row.id)]));
  }
  if (entry.type === 'step' && entry.step.kind === 'tick') {
    const ticked = values[entry.step.id];
    return Array.isArray(ticked) && ticked.length > 0;
  }
  return typeof values[entry.id] === 'string' && values[entry.id] !== '';
}

/** Where a saved form resumes: its first shown screen with no answer. */
export function resumeIndex(sequence, saved) {
  const at = sequence.findIndex((entry) => !isScreenAnswered(entry, saved));
  return at === -1 ? sequence.length : at;
}

export function progressCounts(sequence, saved) {
  return {
    answered: sequence.filter((entry) => isScreenAnswered(entry, saved)).length,
    shown: sequence.length,
  };
}

/**
 * Tick or untick one option of a tick-all step. "None of these" and "Not
 * sure" (options marked exclusive) stand alone: ticking one clears the rest,
 * and ticking anything else clears them.
 */
export function toggleTick(current, optionId, options) {
  const ticked = Array.isArray(current) ? current : [];
  if (ticked.includes(optionId)) return ticked.filter((id) => id !== optionId);
  const exclusive = new Set((options || []).filter((o) => o.exclusive).map((o) => o.id));
  if (exclusive.has(optionId)) return [optionId];
  return [...ticked.filter((id) => !exclusive.has(id)), optionId];
}

/**
 * The answers one support grid row offers (specification section 6). With a
 * ratings step, the eight-answer row: "We don't have this", the ratings under
 * "We have this, and it is:", then "We don't have it, but it would help".
 * Without one, the four-answer row. A row with no would-help twin shows that
 * answer disabled; a grid with no would-help step leaves it out.
 */
export function gridRowAnswers(grid, rowId) {
  const twin = (grid.wouldHelpTwins || {})[rowId] || null;
  const wouldHelp = grid.wouldHelpStep
    ? [{ value: twin || `${rowId}:no-twin`, label: "We don't have it, but it would help", disabled: !twin }]
    : [];
  if (grid.ratingsStep) {
    return [
      { value: GRID_NONE, label: "We don't have this" },
      ...(grid.ratingAnswers || []).map((a) => ({ value: a.id, label: a.text, group: 'have' })),
      ...wouldHelp,
    ];
  }
  return [
    { value: GRID_NONE, label: "We don't have this" },
    { value: GRID_HAVE, label: 'We have this' },
    { value: GRID_NOT_SURE, label: 'Not sure' },
    ...wouldHelp,
  ];
}

/**
 * A screen's words (specification 4.1): the comparison stem once, as the
 * heading, never inside the question; the rest of the stem leads the
 * question, which starts with a capital letter.
 */
export function screenWords(screen) {
  const stem = String((screen && screen.stem) || '').trim();
  const question = String((screen && screen.question) || '').trim();
  let heading = '';
  let lead = stem;
  const match = /^(Compared to [^,]+),\s*(.*)$/.exec(stem);
  if (match) {
    heading = match[1];
    lead = match[2];
  }
  const text = [lead, question].filter(Boolean).join(' ');
  return { heading, text: text ? text.charAt(0).toUpperCase() + text.slice(1) : '' };
}

/** Device class for screen events (specification 9.2). */
export function deviceClass(width) {
  if (width < 640) return 'phone';
  if (width < 1100) return 'tablet';
  return 'desktop';
}
